import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { IndianRupee, Download, FileText, Loader2, RefreshCw, AlertTriangle, TrendingUp, Receipt, Clock, Users, Pencil } from 'lucide-react';
import { isMgr, fmtDate } from './demoConstants';
import DemoPeriodSelector from './DemoPeriodSelector';
import SearchSelect from './SearchSelect';
import DemoDetailModal from './DemoDetailModal';
import DemoDrilldownModal from './DemoDrilldownModal';
import InvoiceOverrideDialog from './InvoiceOverrideDialog';
import { PageHeader, Stat, EmptyState, PreDemoFlag } from './DemoUI';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const MATCH_NOTE = {
  matched: null,
  matched_manual: null,
  no_invoice_after_demo: 'No invoice raised on/after the demo date',
  matched_before_demo: null,
  invoice_before_demo: null,
  invoice_used_by_other_demo: null,
  awaiting_invoice: 'Student linked — invoice not raised yet',
  unlinked: 'Enrolment not linked to a student yet',
};

const csv = (rows) => {
  const head = ['Trainer', 'Student', 'Enrolment No', 'Demo Date', 'Admission Date', 'Invoice No', 'Amount excl GST', 'Amount incl GST', 'GST',
    'Credit Notes excl GST', 'Credit Notes incl GST', 'Net excl GST', 'Paid', 'Pending', 'Other Invoices', 'Counsellor', 'Course', 'Match Basis',
    'Trainer Feedback', 'Counsellor Feedback', 'Demo Code'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.trainer_name, r.student_name, r.enrollment_no, r.demo_date, r.admission_date || '',
    r.invoice_number, r.amount_ex_gst, r.invoice_amount, r.tax_amount, r.cn_ex_gst, r.cn_applied, r.net_ex_gst, r.paid_amount, r.balance, r.other_invoices,
    r.counsellor_name, r.course_name, r.match_basis, r.trainer_feedback_missing ? 'MISSING' : 'Submitted',
    r.counsellor_feedback_missing ? 'MISSING' : 'Submitted', r.demo_code].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

export default function DemoAdmissionRevenuePage({ embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });
  const [f, setF] = useState({ trainer_id: '', counsellor_id: '', course_id: '' });
  const [includeVoid, setIncludeVoid] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const [overrideId, setOverrideId] = useState(null);

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { date_from: period?.from_date, date_to: period?.to_date };
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    if (includeVoid) p.include_void = 'true';
    return p;
  }, [period, f, includeVoid]);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const r = await api.get('/training/demos/reports/admission-revenue', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the revenue report'); }
    finally { setLoading(false); }
  }, [period, params]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post('/training/demos/reports/refresh-invoices');
      toast.success(`${r.data.invoices_checked} invoice(s) re-checked${r.data.cn_alerts ? ` · ${r.data.cn_alerts} new credit note alert(s)` : ' · no new credit notes'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); }
    finally { setBusy(false); }
  };

  const downloadCsv = () => {
    const blob = new Blob([csv(data?.rows || [])], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trainer-admission-revenue-${period?.from_date}_${period?.to_date}.csv`;
    a.click();
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get('/training/demos/reports/admission-revenue.pdf', { params, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `trainer-admission-revenue-${period?.from_date}_${period?.to_date}.pdf`;
      a.click();
    } catch { toast.error('PDF export failed'); }
    finally { setPdfBusy(false); }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];

  const openDrill = (title, subtitle, list) => setDrill({ title, subtitle, rows: list });
  const kpiDrills = {
    admissions: () => openDrill('All admissions', 'Every demo that converted in this period', rows),
    invoiced: () => openDrill('Invoiced admissions', 'Admissions with a matched Books invoice', rows.filter((r) => r.invoice_id)),
    cn: () => openDrill('Credited invoices', 'Admission invoices with a credit note applied — incentive impact', rows.filter((r) => r.cn_applied > 0)),
    awaiting: () => openDrill('Awaiting invoice', 'No invoice counted yet — see the flags for invoices dated before the demo', rows.filter((r) => !r.invoice_id)),
    unpaid: () => openDrill('Pending collection', 'Admission invoices with a balance still to be collected — shown for information, not deducted', rows.filter((r) => r.balance > 0.5)),
    incomplete: () => openDrill('Feedback missing', 'Admissions where the trainer or counsellor has not submitted feedback', rows.filter((r) => r.trainer_feedback_missing || r.counsellor_feedback_missing)),
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="admission-revenue-page">
      <PageHeader embedded={embedded} icon={IndianRupee} eyebrow="Trainer Demo" title="Trainer Admission Revenue"
        subtitle="Invoice value excluding GST from demos that converted — the base incentives are computed on."
        testid="admission-revenue-header">
        {mgr && (
          <Button variant="outline" size="sm" className="h-10" onClick={refresh} disabled={busy} data-testid="revenue-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />Re-check invoices
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-10" onClick={downloadCsv} disabled={!rows.length} data-testid="revenue-export-csv">
          <Download className="w-4 h-4 mr-1.5" />CSV
        </Button>
        <Button size="sm" className="h-10 bg-violet-600 hover:bg-violet-700 text-white" onClick={downloadPdf} disabled={pdfBusy || !rows.length} data-testid="revenue-export-pdf">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}PDF for sign-off
        </Button>
      </PageHeader>
      {t.needs_review > 0 && (
        <div className="mx-auto mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="revenue-needs-review">
          {t.needs_review} admission(s) have an invoice dated before the demo — those within 15 days are counted, older ones are not. Please verify the flagged rows.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="revenue-kpi-rail">
        <Stat icon={Users} label="Admissions" value={t.admissions ?? 0} tone="violet" sub={`${t.matched ?? 0} invoiced`} onClick={kpiDrills.admissions} testid="rkpi-admissions" />
        <Stat icon={Receipt} label="Revenue excl. GST" value={inr(t.ex_gst_total)} tone="emerald" sub={`incl. GST ${inr(t.invoice_total)}`} onClick={kpiDrills.invoiced} testid="rkpi-ex-gst" />
        <Stat icon={AlertTriangle} label="Credit Notes excl. GST" value={inr(t.cn_ex_gst_total)} tone="rose" sub={`${t.cn_flagged ?? 0} invoice(s) credited`} onClick={kpiDrills.cn} testid="rkpi-cn" />
        <Stat icon={TrendingUp} label="Net excl. GST" value={inr(t.net_ex_gst_total)} tone="emerald" sub="after credit notes" testid="rkpi-net" />
        <Stat icon={IndianRupee} label="Pending Collection" value={inr(t.balance_total)} tone="amber" sub={`${t.unpaid ?? 0} invoice(s) part/unpaid`} onClick={kpiDrills.unpaid} testid="rkpi-pending" />
        <Stat icon={Clock} label="Awaiting Invoice" value={t.awaiting_invoice ?? 0} tone="amber" sub="no invoice after demo yet" onClick={kpiDrills.awaiting} testid="rkpi-awaiting" />
      </div>

      <Card className="p-4 border flex flex-wrap items-center gap-2" data-testid="revenue-filter-bar">
        <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
        <SearchSelect value={f.trainer_id} onChange={(v) => setF((p) => ({ ...p, trainer_id: v }))} options={masters.trainers} allowAll allLabel="All trainers" testid="rev-filter-trainer" className="w-44" />
        <SearchSelect value={f.counsellor_id} onChange={(v) => setF((p) => ({ ...p, counsellor_id: v }))} options={masters.counsellors} allowAll allLabel="All counsellors" testid="rev-filter-counsellor" className="w-44" />
        <SearchSelect value={f.course_id} onChange={(v) => setF((p) => ({ ...p, course_id: v }))} options={masters.courses} allowAll allLabel="All courses" testid="rev-filter-course" className="w-48" />
        {mgr && (
          <label className="flex items-center gap-2 text-[11.5px] text-slate-600 pl-1">
            <Switch checked={includeVoid} onCheckedChange={setIncludeVoid} data-testid="rev-include-void" />Include void
          </label>
        )}
        <Button variant="ghost" size="sm" className="h-9 text-[12px] text-slate-500" onClick={() => { setF({ trainer_id: '', counsellor_id: '', course_id: '' }); setIncludeVoid(false); }} data-testid="rev-clear-filters">Clear</Button>
      </Card>

      <Card className="px-4 py-3 border border-slate-200 bg-slate-50/70" data-testid="revenue-rule-note">
        <p className="text-[11.5px] text-slate-600">
          <b>How the amount is picked:</b> {data?.rule || 'The incentive base is the invoice value excluding GST — earliest non-void invoice dated on/after the demo date, matched via the student’s Zoho customer id.'}{' '}
          Void, rejected and draft invoices never count. Credit notes are re-checked <b>every day at 04:10 IST</b> — a credited invoice is flagged in red so an incentive can be clawed back.
          {mgr ? <> Use the <b>pencil</b> on a row to pin a different invoice when Books picked the wrong one.</> : null}
        </p>
      </Card>

      <section className="space-y-3" data-testid="revenue-trainer-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Trainer summary</h2>
        <Card className="border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                <th className="px-4 py-3">Trainer</th><th className="px-4 py-3 text-right">Admissions</th>
                <th className="px-4 py-3 text-right">Invoiced</th><th className="px-4 py-3 text-right">Awaiting</th>
                <th className="px-4 py-3 text-right">Revenue excl. GST</th><th className="px-4 py-3 text-right">Incl. GST</th><th className="px-4 py-3 text-right">CN excl. GST</th>
                <th className="px-4 py-3 text-right">Net excl. GST</th><th className="px-4 py-3 text-right">Pending</th><th className="px-4 py-3 text-right">Avg Ticket</th>
              </tr></thead>
              <tbody>
                {(data?.by_trainer || []).map((g) => (
                  <tr key={g.name} onClick={() => openDrill(g.name, `${g.admissions} admission(s) · ${inr(g.ex_gst_total)} excl. GST in this period`, rows.filter((r) => r.trainer_name === g.name))}
                    className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors ${g.cn_total > 0 ? 'bg-rose-50/40' : ''}`} data-testid={`revenue-trainer-row-${g.id || g.name}`}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{g.name}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{g.admissions}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{g.matched}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-700">{g.awaiting || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(g.ex_gst_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inr(g.invoice_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-rose-700">{g.cn_ex_gst_total ? `-${inr(g.cn_ex_gst_total)}` : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">{inr(g.net_ex_gst_total)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${g.balance_total > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{g.balance_total > 0 ? inr(g.balance_total) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{inr(g.avg_ticket)}</td>
                  </tr>
                ))}
                {(data?.by_trainer || []).length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-center text-[12px] text-slate-400">No admissions in this period.</td></tr>}
                {(data?.by_trainer || []).length > 0 && (
                  <tr className="bg-violet-50 font-semibold" data-testid="revenue-trainer-total">
                    <td className="px-4 py-3 text-slate-900">TOTAL</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.admissions}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.matched}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.awaiting_invoice || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-900">{inr(t.ex_gst_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inr(t.invoice_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-rose-700">{t.cn_ex_gst_total ? `-${inr(t.cn_ex_gst_total)}` : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(t.net_ex_gst_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-700">{t.balance_total > 0 ? inr(t.balance_total) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{inr(t.avg_ticket)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <section className="space-y-3" data-testid="revenue-detail-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">
          Admission detail <span className="text-slate-400 normal-case">({rows.length})</span>
          {t.feedback_incomplete ? <button onClick={kpiDrills.incomplete} className="ml-2 text-[11px] font-medium text-amber-700 normal-case hover:underline" data-testid="revenue-incomplete-link">· {t.feedback_incomplete} with feedback missing</button> : null}
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading revenue…</div>
        ) : rows.length === 0 ? (
          <Card className="border"><EmptyState title="No admissions in this period" hint="An admission appears here once its enrolment number is linked and an invoice is raised on/after the demo date." /></Card>
        ) : (
          <Card className="border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-4 py-3">Trainer</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Demo</th>
                  <th className="px-4 py-3">Admission</th><th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3 text-right">Amount excl. GST</th><th className="px-4 py-3">Counsellor</th><th className="px-4 py-3">Flags</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const fbMissing = r.trainer_feedback_missing || r.counsellor_feedback_missing;
                    const tone = !r.invoice_id ? 'bg-amber-50/60' : r.cn_applied > 0 ? 'bg-rose-50/50' : fbMissing ? 'bg-slate-50' : '';
                    return (
                      <tr key={r.id} className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors ${tone}`} data-testid={`revenue-row-${r.id}`}>
                        <td className="px-4 py-3 font-semibold text-slate-800">{r.trainer_name}</td>
                        <td className="px-4 py-3">
                          <span className="text-slate-800">{r.student_name}</span>
                          {r.enrollment_no ? <span className="block font-mono text-[10.5px] text-slate-400">#{r.enrollment_no}</span> : null}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => setOpenId(r.id)} className="font-mono text-[11.5px] text-violet-700 hover:underline" data-testid={`revenue-demo-link-${r.id}`}>{r.demo_code}</button>
                          <span className="block text-[10.5px] text-slate-400">{fmtDate(r.demo_date)}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.admission_date ? fmtDate(r.admission_date) : <span className="text-amber-700">—</span>}
                          {typeof r.days_to_admission === 'number' ? <span className="block text-[10.5px] text-slate-400">{r.days_to_admission < 0 ? `${-r.days_to_admission}d before demo` : `${r.days_to_admission}d after demo`}</span> : null}
                        </td>
                        <td className="px-4 py-3">
                          {r.invoice_number
                            ? <span className="font-mono text-[11px] text-slate-700">{r.invoice_number}</span>
                            : !r.earlier_invoice && !r.invoice_credited_to && <span className="text-[11px] text-amber-700 mr-1.5">not raised yet</span>}
                          <PreDemoFlag r={r} />
                          {r.invoice_status ? <span className="block text-[10px] uppercase tracking-wider text-slate-400">{r.invoice_status}</span> : null}
                          {mgr && (
                            <button onClick={() => setOverrideId(r.id)} className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline" title="Pick a different invoice" data-testid={`revenue-override-${r.id}`}>
                              <Pencil className="w-2.5 h-2.5" />{r.override ? 'change pick' : 'pick'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="tabular-nums font-semibold text-slate-900">{inr(r.amount_ex_gst)}</span>
                          {r.invoice_id ? <span className="block tabular-nums text-[10px] text-slate-400">{inr(r.invoice_amount)} incl. GST</span> : null}
                          {r.cn_ex_gst > 0 && <span className="block tabular-nums text-[10.5px] text-rose-700">CN -{inr(r.cn_ex_gst)} → {inr(r.net_ex_gst)}</span>}
                          {r.balance > 0.5 && <span className="block tabular-nums text-[10.5px] text-amber-700">{inr(r.balance)} pending</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{r.counsellor_name}</td>
                        <td className="px-4 py-3">
                          <span className="flex flex-wrap gap-1">
                            {r.cn_applied > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200" title={`Credit note(s): ${(r.cn_numbers || []).join(', ')}`} data-testid={`flag-cn-${r.id}`}>CREDIT NOTE</span>}
                            {r.trainer_feedback_missing && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-testid={`flag-tfb-${r.id}`}>Trainer FB missing</span>}
                            {r.counsellor_feedback_missing && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-testid={`flag-cfb-${r.id}`}>Counsellor FB missing</span>}
                            {r.other_invoices > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200" title="This student has other invoices — only the admission invoice is credited">+{r.other_invoices} other inv</span>}
                            {r.match_basis === 'enrolment_no' && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200" title="Matched on the enrolment number, not the Zoho customer id">enrol-no match</span>}
                            {r.override && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200" title={`Pinned by ${r.override.by}: ${r.override.reason || ''}`} data-testid={`flag-override-${r.id}`}>manual pick</span>}
                            {MATCH_NOTE[r.match_state] && <span className="text-[10.5px] text-amber-700">{MATCH_NOTE[r.match_state]}</span>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <DemoDrilldownModal open={!!drill} onClose={() => setDrill(null)} title={drill?.title} subtitle={drill?.subtitle}
        rows={drill?.rows || []} variant="revenue" onOpenDemo={(id) => { setDrill(null); setOpenId(id); }} />
      <InvoiceOverrideDialog demoId={overrideId} onClose={() => setOverrideId(null)} onSaved={load} />
      <DemoDetailModal demoId={openId} readOnly={!mgr} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
